import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getSalesPersona } from '@/api/clarityLens/salesLens'
import { ScalrTableColumn } from '@/components/customComponents'
import { pushNotification, useAppDispatch } from '@/store'
import { areFilterParamsValid } from '@/utils/filterParams'
import { SalesPersonaModel } from '@/types/clarityLens'

const SALES_PERSONA_COLUMNS: ScalrTableColumn[] = [
  {
    header: 'Persona',
    accessorKey: 'persona',
    type: 'text',
  },
  {
    header: 'Avg. Transaction Count',
    accessorKey: 'avgTransactionCount',
    type: 'number',
  },
  {
    header: 'Most Used Modifier',
    accessorKey: 'mostUsedModifier',
    type: 'text',
  },
  {
    header: '% of Sales',
    accessorKey: 'salesPercentage',
    type: 'percentage',
  },
  {
    header: 'Avg. Order Value',
    accessorKey: 'avgOrderValue',
    type: 'money',
  },
  {
    header: 'Customer Reach',
    accessorKey: 'customerReach',
    type: 'number',
  },
]

export default function useSalesPersona(
  startDate: string,
  endDate: string,
  branches: string,
  itemGroupName: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () =>
      areFilterParamsValid(startDate, endDate, branches) &&
      Boolean(itemGroupName?.trim()),
    [startDate, endDate, branches, itemGroupName]
  )
  const loading = !isReady || isFetching

  const [salesPersonaData, setSalesPersonaData] =
    useState<SalesPersonaModel[]>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      const data = await fetchSalesPersonaData(
        branches,
        startDate,
        endDate,
        itemGroupName
      )

      setSalesPersonaData(data)
    } catch (err) {
      const errorInstance = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: errorInstance.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [isReady, branches, startDate, endDate, itemGroupName])

  const refetch = useCallback(() => {
    console.log('Refetching sales persona data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    salesPersonaData,
    SALES_PERSONA_COLUMNS,
    loading,
    refetch,
  }
}

async function fetchSalesPersonaData(
  branches: string,
  startDate: string,
  endDate: string,
  itemGroupName: string
): Promise<SalesPersonaModel[]> {
  try {
    const response = await getSalesPersona(
      branches,
      startDate,
      endDate,
      itemGroupName
    )

    const data = response.map((responseItem) => {
      return {
        persona: responseItem.persona,
        avgTransactionCount: responseItem.avg_transaction_count,
        mostUsedModifier: responseItem.most_used_modifier,
        salesPercentage: responseItem.sales_percentage,
        avgOrderValue: responseItem.avg_order_value,
        customerReach: responseItem.customer_reach,
      }
    })

    return data
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}
